"use client";

import React, { Component, ReactNode } from 'react';
import { Alert } from '@/ui';
import { colors, spacing, radius, textStyles, primitives } from '@/ui/tokens';
import { Refresh, ErrorOutline } from '@mui/icons-material';

import { logger } from '@/lib/sola/logger';

interface Props {
  children: ReactNode;
  toolName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary for Tool Components
 *
 * Prevents a single tool crash from breaking the entire chat interface.
 * Logs errors and displays user-friendly error UI with retry option.
 */
export class ToolErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    logger.error('Tool component crashed', error, {
      toolName: this.props.toolName,
      componentStack: errorInfo.componentStack,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={{
            marginTop: spacing[2],
            border: `1px solid ${colors.signal.dangerLight}`,
            borderRadius: radius.md,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: spacing[2],
              padding: `${spacing[3]}px ${spacing[4]}px`,
              backgroundColor: colors.signal.dangerLight,
            }}
          >
            <ErrorOutline style={{ fontSize: 18, color: colors.signal.danger }} />
            <span style={{ ...textStyles.body2, fontWeight: 600, color: colors.signal.danger }}>
              Display Error
            </span>
          </div>

          {/* Body */}
          <div style={{ padding: spacing[4] }}>
            <span style={{ ...textStyles.body2, color: colors.text.muted, display: "block", marginBottom: spacing[3] }}>
              {this.props.toolName ? (
                <>The <strong>{this.props.toolName}</strong> display crashed while rendering. The tool may have returned unexpected data.</>
              ) : (
                <>This tool display crashed while rendering. The data format may be unexpected.</>
              )}
            </span>

            {/* Retry button */}
            <button
              onClick={this.handleRetry}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: spacing[1],
                padding: `${spacing[1]}px ${spacing[3]}px`,
                border: `1px solid ${colors.border.subtle}`,
                borderRadius: radius.sm,
                backgroundColor: "transparent",
                cursor: "pointer",
                ...textStyles.caption,
                fontWeight: 500,
                color: colors.text.muted,
              }}
            >
              <Refresh style={{ fontSize: 14 }} />
              Retry
            </button>

            {/* Dev error details */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre
                style={{
                  marginTop: spacing[3],
                  padding: spacing[3],
                  backgroundColor: primitives.neutral[100],
                  borderRadius: radius.sm,
                  ...textStyles.caption,
                  fontFamily: 'monospace',
                  overflow: 'auto',
                  maxHeight: 200,
                  color: colors.signal.danger,
                }}
              >
                {this.state.error.message}
              </pre>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
